/**
 * See the NOTICE file distributed with this work for additional information
 * regarding copyright ownership.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState, memo } from 'react';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import config from 'config';
import * as urlFor from 'src/shared/helpers/urlHelper';

import { useAppDispatch } from 'src/store';

import { getFormattedDateTime } from 'src/shared/helpers/formatters/dateFormatter';
import { areVepSubmissionResultsExpired } from 'src/content/app/tools/vep/utils/vepResultsAvailability';

import { fillVepFormWithExistingSubmissionData } from 'src/content/app/tools/vep/state/vep-form/vepFormSlice';
import { deleteSubmission } from 'src/content/app/tools/vep/state/vep-submissions/vepSubmissionsSlice';

import {
  serverSideSubmissionStatuses,
  type VepSubmission
} from 'src/content/app/tools/vep/types/vepSubmission';

import { PrimaryButton } from 'src/shared/components/button/Button';
import TextButton from 'src/shared/components/text-button/TextButton';
import ButtonLink from 'src/shared/components/button-link/ButtonLink';
import DeleteButton from 'src/shared/components/delete-button/DeleteButton';
import QuestionButton from 'src/shared/components/question-button/QuestionButton';
import UnavailableResults from 'src/content/app/tools/shared/components/help-messages/UnavailableResults';
import { ControlledDownloadButton } from 'src/shared/components/download-button/DownloadButton';

import { UNAVAILABLE_RESULTS_WARNING } from 'src/content/app/tools/shared/constants/displayedMessages';

import { LoadingState } from 'src/shared/types/loading-state';

import styles from './VepSubmissionHeader.module.css';

type Props = {
  submission: {
    id: VepSubmission['id'];
    submittedAt: VepSubmission['submittedAt'];
    status: VepSubmission['status'];
  };
  filtersString?: string; // filters might be applied on the page for submission results; they influence what is downloaded
};

const VepSubmissionHeader = (props: Props) => {
  const { submission } = props;
  const [isDeleting, setIsDeleting] = useState(false);
  const [shouldShowDownloadOptions, setShouldShowDownloadOptions] =
    useState(false);
  const submissionTime = submission.submittedAt
    ? getFormattedDateTime(new Date(submission.submittedAt))
    : null;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const toggleDeletionConfirmation = () => {
    setIsDeleting(!isDeleting);
  };

  const toggleDownloadOptions = () => {
    setShouldShowDownloadOptions(!shouldShowDownloadOptions);
  };

  const onEditSubmission = () => {
    dispatch(
      fillVepFormWithExistingSubmissionData({ submissionId: submission.id })
    );
    navigate(urlFor.vepForm());
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.light}>Ensembl VEP analysis</div>
        <div className={styles.submissionId}>
          {hasServerSideSubmissionId(submission) && (
            <>
              <span className={classNames(styles.labelLeft, styles.smallLight)}>
                Submission
              </span>{' '}
              {submission.id}
            </>
          )}
        </div>
        <div className={styles.rerun}>
          <TextButton onClick={onEditSubmission}>Edit/rerun</TextButton>
        </div>
        <div className={styles.submissionDate}>
          {submissionTime} <span className={styles.timezone}>GMT</span>
        </div>
        <ControlButtons
          {...props}
          isDeleting={isDeleting}
          areDownloadOptionsShowing={shouldShowDownloadOptions}
          onDelete={toggleDeletionConfirmation}
          onDownload={toggleDownloadOptions}
        />
      </div>
      {isDeleting && (
        <div className={styles.confirmationContainer}>
          <DeletionConfirmation
            {...props}
            onCancel={toggleDeletionConfirmation}
          />
        </div>
      )}
      {shouldShowDownloadOptions && (
        <DownloadOptions {...props} onCancel={toggleDownloadOptions} />
      )}
    </>
  );
};

const hasServerSideSubmissionId = (submission: { status: string }) => {
  // Only submissions with a server-side status will have an id assigned by the server
  // (temporary client-side ids aren't helpful for users; so there's no point in displaying those)
  return (serverSideSubmissionStatuses as readonly string[]).includes(
    submission.status
  );
};

const ControlButtons = (
  props: Props & {
    isDeleting: boolean;
    areDownloadOptionsShowing: boolean;
    onDelete: () => void;
    onDownload: () => void;
  }
) => {
  const {
    submission,
    isDeleting,
    areDownloadOptionsShowing,
    onDelete,
    onDownload
  } = props;

  const canGetResults = submission.status === 'SUCCEEDED';
  const vepResultsLink = urlFor.vepResults({
    submissionId: props.submission.id
  });
  const isExpiredSubmission = areVepSubmissionResultsExpired(submission);

  if (isExpiredSubmission) {
    return (
      <div className={styles.controls}>
        <DeleteButton onClick={onDelete} disabled={isDeleting} />
        <div className={styles.errorMessage}>
          <span>{UNAVAILABLE_RESULTS_WARNING}</span>
          <QuestionButton helpText={<UnavailableResults />} />
        </div>
      </div>
    );
  } else {
    return (
      <div className={styles.controls}>
        <DeleteButton
          onClick={onDelete}
          disabled={isDeleting}
          aria-label="Delete this submission"
        />

        <ControlledDownloadButton
          onClick={onDownload}
          status={LoadingState.NOT_REQUESTED}
          disabled={isDeleting}
          aria-label="Show download options"
          aria-expanded={areDownloadOptionsShowing}
        />

        <ButtonLink
          isDisabled={isDeleting || !canGetResults}
          to={vepResultsLink}
        >
          Results
        </ButtonLink>
      </div>
    );
  }
};

const DeletionConfirmation = (
  props: Props & {
    onCancel: () => void;
  }
) => {
  const { submission, onCancel } = props;
  const dispatch = useAppDispatch();

  const onDelete = () => {
    dispatch(deleteSubmission({ submissionId: submission.id }));
  };

  return (
    <div className={styles.deletionConfirmation}>
      <span className={styles.deletionConfirmationMessage}>
        Delete this submission?
      </span>
      <PrimaryButton onClick={onDelete}>Delete</PrimaryButton>
      <TextButton onClick={onCancel}>Do not delete</TextButton>
    </div>
  );
};

const DownloadOptions = ({
  submission,
  onCancel,
  filtersString
}: {
  submission: Props['submission'];
  filtersString?: Props['filtersString'];
  onCancel: () => void;
}) => {
  // Raw VCF vs a flattened, fully-expanded table that is spreadsheet-friendly
  const vcfDownloadLink = buildDownloadLink({
    submission,
    filtersString
  });
  const tsvDownloadLink = buildDownloadLink({
    submission,
    filtersString,
    format: 'tsv'
  });

  return (
    <div className={styles.confirmationContainer}>
      <div className={styles.downloadOptionsWrapper}>
        <span className={styles.downloadLabel}>Download:</span>
        <div className={styles.downloadOptions}>
          <a className={styles.option} href={vcfDownloadLink} download={true}>
            VCF
          </a>
          <a className={styles.option} href={tsvDownloadLink} download={true}>
            TSV
          </a>
        </div>

        <TextButton onClick={onCancel}>Close</TextButton>
      </div>
    </div>
  );
};

const buildDownloadLink = ({
  submission,
  format,
  filtersString
}: {
  submission: Props['submission'];
  format?: string;
  filtersString?: string;
}) => {
  let url = `${config.toolsApiBaseUrl}/vep/submissions/${submission.id}/download`;
  const searchParams = new URLSearchParams();
  if (format) {
    searchParams.set('format', format);
  }
  if (filtersString) {
    searchParams.set('filters', filtersString);
  }
  const queryString = searchParams.toString();
  if (queryString) {
    url = `${url}?${queryString}`;
  }

  return url;
};

export default memo(VepSubmissionHeader);
